import { IApiRequestOptions } from '@import-io/doc-sdk';
import { CrawlRun } from '@import-io/types';
import { ISnapshotImportStageStatus } from '_/features/snapshots/snapshotPage/snapshotPage.types';
import { ISnapshot } from '_/types/snapshots/snapshot.types';
import zeusSdk from '_/utils/SDKs';

export const updateSnapshotLabels = async (snapshotId: string, labels: string[]): Promise<ISnapshot> => {
  return (await zeusSdk.snapshots.patch(snapshotId, { labels: labels })) as ISnapshot;
};

export const crawlRunRequest = async (snapshotId: string): Promise<CrawlRun | null> => {
  const response = await zeusSdk.snapshots.getCrawlRun(snapshotId);
  // Check if response is a raw Response object that needs parsing
  if (response instanceof Response) {
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    // Check if response has content before parsing
    const text = await response.text();
    if (!text || text.trim() === '') {
      // Empty response is normal for snapshots without crawl run data (e.g., chunked snapshots)
      return null;
    }
    try {
      return JSON.parse(text);
    } catch (error) {
      console.error('Error parsing crawl run JSON:', error);
      return null;
    }
  }
  return response;
};

export const getSnapshotList = async (options?: IApiRequestOptions): Promise<ISnapshot[]> => {
  const { items } = await zeusSdk.snapshots.list(options);
  return items as ISnapshot[];
};

export const resetImportJob = async (snapshotId: string): Promise<ISnapshot | null> => {
  try {
    return await zeusSdk.snapshots.resetImportJob(snapshotId);
  } catch {
    return null;
  }
};

export const fetchSnapshotChunks = async (
  snapshotId: string,
  limit = 50,
  cursor?: string,
): Promise<{ items: ISnapshot[]; cursor?: string }> => {
  const options: IApiRequestOptions = {
    queryParameters: {
      limit: limit,
      sort: 'startAt',
    },
    include: 'editor,assignedUser,importStatus,Source.Collection.Project,Delivery.Flow,parent',
  };

  if (cursor && options.queryParameters) {
    options.queryParameters.cursor = cursor;
  }

  const result = await zeusSdk.snapshots.getChunks(snapshotId, options);
  return {
    items: result?.items || [],
    cursor: result?.metadata?.cursor,
  };
};

export const getImportStatus = async (id: string): Promise<ISnapshotImportStageStatus[]> => {
  const { importStatus } = await zeusSdk.snapshots.getChildren(id, 'importstatus');
  return importStatus?.pipeline || [];
};

export const fetchSnapshot = async (snapshotId: string): Promise<ISnapshot> => {
  const snapshot = await zeusSdk.snapshots.get(snapshotId, {
    include:
      'source.collection.project,importStatus,editor,assignedUser,parent,login.loginpool,delivery.flowDelivery,previousSnapshot.source.collection',
  });
  return snapshot as ISnapshot;
};

export const fetchSnapshotComments = async (snapshotId: string): Promise<any[]> => {
  const comments = await zeusSdk.snapshots.getChildren(snapshotId, 'comments', {
    queryParameters: {
      limit: 1000,
      sort: 'addedAt',
      sortDirection: 'DESC',
    },
    include: 'editor',
  });
  return comments?.items || [];
};

export const createSnapshotComment = async (snapshotId: string, comment: string): Promise<any> => {
  return zeusSdk.snapshots.postComment(snapshotId, comment);
};

export const fetchSnapshotStateTransitions = async (snapshotId: string): Promise<any[]> => {
  try {
    // Try using getTransitions method like sources do
    const response = await (zeusSdk.snapshots as any).getTransitions?.(snapshotId);
    if (response?.items) {
      return response.items;
    }

    // Fallback to getChildren if getTransitions doesn't exist
    const transitions = await zeusSdk.snapshots.getChildren(snapshotId, 'statetransitions', {
      queryParameters: {
        sort: 'occurredAt',
        sortDirection: 'DESC',
      },
    });

    // Map the API response fields to match what StateTransitions component expects
    return (transitions?.items || []).map((item: any) => ({
      timestamp: item.occurredAt || item.timestamp,
      editor: item.editor,
      before: item.before,
      after: item.after,
      timeInStatus: item.timeInStatus,
    }));
  } catch (error) {
    console.error(`Error fetching snapshot state transitions for ${snapshotId}`, error);
    return [];
  }
};

export const fetchSnapshotDestinationPushes = async (snapshotId: string): Promise<any[]> => {
  try {
    const pushes = await zeusSdk.snapshots.getChildren(snapshotId, 'pushes', {
      include: 'destination',
    });
    return pushes?.items || [];
  } catch (error) {
    console.error(`Error fetching snapshot destination pushes for ${snapshotId}`, error);
    return [];
  }
};

export const fetchSnapshotHistory = async (snapshotId: string, cursor?: string): Promise<any> => {
  const options: IApiRequestOptions = {
    queryParameters: {
      limit: 20,
      sort: 'occurredAt',
      sortDirection: 'DESC',
    },
  };

  if (cursor && options.queryParameters) {
    options.queryParameters.cursor = cursor;
  }

  const history = await zeusSdk.snapshots.getChildren(snapshotId, '_history', options);
  return {
    items: history?.items || [],
    cursor: history?.cursor,
  };
};

export const fetchSnapshotChecks = async (snapshotId: string): Promise<any[]> => {
  try {
    const response = await zeusSdk.snapshots.getChecks(snapshotId);
    return response?.items || [];
  } catch (error) {
    console.error('Error fetching snapshot checks', error);
    return [];
  }
};

export const updateSnapshotCheck = async (snapshotId: string, collectionQaTestId: string, snapshotCheck: any): Promise<any> => {
  return zeusSdk.snapshots.updateCheck(snapshotId, collectionQaTestId, snapshotCheck);
};

export const updateSnapshotCheckTestCase = async (
  snapshotId: string,
  testCaseId: string,
  snapshotCheckTestCase: any,
): Promise<any> => {
  return zeusSdk.snapshots.updateCheckTestCase(snapshotId, testCaseId, snapshotCheckTestCase);
};

export const resolveSnapshotChecks = async (snapshotId: string): Promise<any> => {
  return zeusSdk.snapshots.resolveTests(snapshotId);
};

export const revertSnapshotChecks = async (snapshotId: string): Promise<any> => {
  return zeusSdk.snapshots.revertTests(snapshotId);
};

/**
 * Update snapshot status
 */
export const updateSnapshotStatus = async (snapshotId: string, status: string): Promise<ISnapshot> => {
  return zeusSdk.snapshots.patch(snapshotId, { status: status });
};

/**
 * Stop a snapshot run
 */
export const stopSnapshotRun = async (snapshotId: string): Promise<any> => {
  return zeusSdk.snapshots.stopSnapshotRun(snapshotId);
};

/**
 * Import a snapshot
 */
export const importSnapshot = async (snapshotId: string): Promise<any> => {
  return zeusSdk.snapshots.importData(snapshotId);
};

/**
 * Retry a snapshot with options
 */
export const retrySnapshot = async (snapshotId: string, retryOptions: any): Promise<any> => {
  return zeusSdk.snapshots.retrySnapshot(snapshotId, retryOptions, {});
};

/**
 * Re-extract a snapshot
 */
export const reextractSnapshot = async (snapshotId: string): Promise<any> => {
  return zeusSdk.snapshots.reextractSnapshot(snapshotId);
};

/**
 * Re-import a snapshot
 */
export const reimportSnapshot = async (snapshotId: string): Promise<any> => {
  return zeusSdk.snapshots.reimportSnapshot(snapshotId);
};

/**
 * Re-run a snapshot
 */
export const rerunSnapshot = async (snapshotId: string): Promise<any> => {
  return zeusSdk.snapshots.rerunSnapshot(snapshotId);
};

/**
 * Re-run transforms on a snapshot
 */
export const rerunTransforms = async (snapshotId: string): Promise<any> => {
  return zeusSdk.snapshots.rerunTransforms(snapshotId);
};

/**
 * Regenerate assets for a snapshot
 */
export const regenerateAssets = async (snapshotId: string): Promise<any> => {
  return zeusSdk.snapshots.regenerateAssets(snapshotId);
};

/**
 * Update snapshot with partial data
 */
export const updateSnapshot = async (snapshotId: string, snapshot: any): Promise<ISnapshot> => {
  return zeusSdk.snapshots.patch(snapshotId, snapshot);
};

/**
 * Unassign snapshot from user
 */
export const unassignSnapshot = async (snapshotId: string): Promise<any> => {
  return zeusSdk.snapshots.unassign(snapshotId);
};
